import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { userHasFreeTier } from '@wetransfer/transfer/helper/user';
import { usePrevious } from '@wetransfer/transfer/hooks/usePrevious';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import Router from '@wetransfer/transfer/modules/router';
import Actions from '@wetransfer/transfer/store/action';
import { selectAccountHasActiveSubsciption } from '@wetransfer/transfer/store/selector/account';
import { selectIsConsentCookieOverlayActive } from '@wetransfer/transfer/store/selector/consent';
import { selectIsSessionTandC } from '@wetransfer/transfer/store/selector/session';
import { selectTransferParentID } from '@wetransfer/transfer/store/selector/transfer';
import {
  selectUserHasOnboarded,
  selectUserIsLoggedIn,
  selectUserIsVerified,
  selectUser,
} from '@wetransfer/transfer/store/selector/user';

import {
  TransferMode,
  TransferWindowRoute,
  UseRouteTransitionTypes,
  UseTransferModeProps,
} from './TransferWindow.types';

export const useTransferMode = ({
  transferId,
  transferSecret,
}: UseTransferModeProps): TransferMode => {
  const isSessionTandC = useSelector(selectIsSessionTandC);
  const isCookieOverlayActive = useSelector(selectIsConsentCookieOverlayActive);
  const transferParentID = useSelector(selectTransferParentID);
  const [transferMode, setTransferMode] = useState<TransferMode | null>(null);
  const isDownload = transferId && transferSecret && !transferParentID; // transferParentID means forwarded transfer <-- upload

  const isTandCExperiment =
    !Browser.isMobile &&
    !isSessionTandC &&
    (!isCookieOverlayActive || isDownload);
  const isTandC = Browser.isMobile && !isSessionTandC;

  useEffect(() => {
    if (isDownload && !isTandCExperiment && !isTandC) {
      setTransferMode('download');
    } else if (isTandCExperiment) {
      setTransferMode('tandc-experiment');
    } else if (isTandC) {
      setTransferMode('tandc');
    } else {
      setTransferMode('upload');
    }
  }, [isDownload, isTandCExperiment, isTandC]);

  return transferMode;
};

export const useRouteTransition = (
  routeType: TransferWindowRoute['type']
): UseRouteTransitionTypes => {
  const halfPanel = routeType === 'plus';
  const fullPanel = routeType === 'page';
  const hidden = !routeType;

  return {
    halfPanel,
    fullPanel,
    hidden,
  };
};

export const useClearTransferWindowNotifications = (
  routeType: TransferWindowRoute['type']
): void => {
  const dispatch = useDispatch();
  const prevRouteType = usePrevious(routeType);

  const routeTypeWasTransfer =
    prevRouteType === 'transfer' && routeType !== 'transfer';

  useEffect(() => {
    // Should we clear any notifications? (When panel opens)
    if (routeTypeWasTransfer) {
      dispatch(Actions.Notification.clearTransfer());
    }
  }, [routeTypeWasTransfer, dispatch]);
};

export const useOnboardingRedirect = (
  routeType: TransferWindowRoute['type']
): void => {
  const routeTypeIsTransfer = routeType === 'transfer';
  const user = useSelector(selectUser);

  const accountHasActiveSubscription = useSelector(
    selectAccountHasActiveSubsciption
  );
  const userHasOnboarded = useSelector(selectUserHasOnboarded);
  const userIsLoggedIn = useSelector(selectUserIsLoggedIn);
  const userIsVerified = useSelector(selectUserIsVerified);
  const isUserFreeTier = userHasFreeTier(user);

  useEffect(() => {
    if (
      routeTypeIsTransfer &&
      !userHasOnboarded &&
      userIsLoggedIn &&
      userIsVerified &&
      accountHasActiveSubscription
    ) {
      if (isUserFreeTier) {
        Router.navigateTo('/sign-up/completed');
      } else {
        Router.navigateTo('/payment/completed');
      }
    }
  }, [
    accountHasActiveSubscription,
    isUserFreeTier,
    routeTypeIsTransfer,
    userHasOnboarded,
    userIsLoggedIn,
    userIsVerified,
  ]);
};
